import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { BoatService } from '../services/boat.service';
import { getBoatImageUrl } from '../utils/image-url.util';

@Component({
  selector: 'app-pencarian',
  templateUrl: './pencarian.component.html',
  styleUrls: ['./pencarian.component.css']
})
export class PencarianComponent implements OnInit, OnDestroy {
  @Input() datesOnly = false;
  searchText = '';
  preservedSelections: Record<string, string[]> = {};
  selectedTripKategori = '';
  selectedBoatKategori = '';
  selectedTujuan = '';
  startDate = '';
  endDate = '';
  errorMessage = '';
  filteredBoats: any[] = [];
  showSuggestions = false;
  loading = false;
  private searchSubject = new Subject<string>();
  private destroyed = new Subject<void>();
  readonly minDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Makassar', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date());

  constructor(private boatService: BoatService, private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroyed)).subscribe(params => {
      const readable = (value: any) => typeof value === 'string' ? value.replace(/-/g, ' ') : '';
      this.searchText = params['keyword'] ?? readable(params['nama_kapal']);
      const match = (key: string, choices: string[]) => {
        const value = params[key];
        const values = (Array.isArray(value) ? value : value ? [value] : []).map(v => choices.find(x => x.toLowerCase() === readable(v).toLowerCase()) || readable(v));
        this.preservedSelections[key] = values;
        return values.length > 1 ? '__multiple__' : values[0] || '';
      };
      this.selectedTripKategori = match('trip_kategori', ['Boat Charter', 'Open Trip Komodo National Park', 'Open Trip Outside Komodo National Park']);
      this.selectedBoatKategori = match('boat_kategori', ['Deluxe Boat', 'Speed Boat', 'VIP Boat', 'VVIP Boat']);
      this.selectedTujuan = match('tujuan', ['Alor', 'Banda Island', 'Komodo National Park', 'Raja Ampat']);
      this.startDate = params['date_from'] || '';
      this.endDate = params['date_to'] || '';
    });
    this.searchSubject.pipe(
      debounceTime(300),
      switchMap(keyword => {
        this.loading = keyword.length >= 3;
        this.showSuggestions = this.loading;
        return this.loading ? this.boatService.cariKapal(keyword).pipe(catchError(() => of({ items: [] }))) : of({ items: [] });
      }), takeUntil(this.destroyed)
    ).subscribe(results => {
      this.filteredBoats = results.items || [];
      this.loading = false;
    });
  }

  onSearchChange(): void { this.searchSubject.next(this.searchText.trim()); }
  hideSuggestions(): void { this.showSuggestions = false; }
  selectBoat2(boat: any): void { this.searchText = boat.nama_kapal; this.hideSuggestions(); }
  getImageUrl(path: string): string { return path ? getBoatImageUrl(path) : 'assets/img/placeholder.jpg'; }
  onStartDateChange(): void {
    if (this.endDate && this.endDate < this.startDate) this.endDate = '';
    this.errorMessage = '';
  }
  onSubmit(): void {
    this.hideSuggestions();
    this.errorMessage = '';
    if (this.endDate && !this.startDate) {
      this.errorMessage = 'Choose a departure date before an end date.';
      return;
    }
    if (this.startDate && (this.startDate < this.minDate || (this.endDate && this.endDate < this.startDate))) {
      this.errorMessage = 'Choose a future departure date and an end date on or after it.';
      return;
    }
    const selection = (key: string, value: string) => value === '__multiple__' ? this.preservedSelections[key] : value || null;
    const queryParams: any = this.datesOnly ? {
      date_from: this.startDate || null,
      date_to: this.endDate || null
    } : {
      ...this.route.snapshot.queryParams,
      page: null,
      retry: null,
      nama_kapal: null,
      keyword: this.searchText.trim() || null,
      trip_kategori: selection('trip_kategori', this.selectedTripKategori),
      boat_kategori: selection('boat_kategori', this.selectedBoatKategori),
      tujuan: selection('tujuan', this.selectedTujuan),
      date_from: this.startDate || null,
      date_to: this.endDate || null,
      departure_time: null
    };
    this.router.navigate(['/search-results'], { queryParams });
  }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
}
